import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useToasts } from "react-toast-notifications";
import axios from "axios";
import { IconButton, InputAdornment, TextField } from "@mui/material";
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

const PChangePassword = ({ user, showHeader = true }) => {
    const { addToast } = useToasts();

    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    const formik = useFormik({
        initialValues: {
            current: "",
            password: "",
        },
        validationSchema: Yup.object({
            current: Yup.string().required("Current password is required"),
            password: Yup.string()
                .min(8, "Minimum 8 characters")
                .required("New password is required"),
        }),
        onSubmit: async (values, { setErrors }) => {
            setIsLoading(true);
            await axios
                .patch(
                    `/api/auth/${localStorage.getItem(
                        "loggedin_user"
                    )}/password`,
                    { ...values }
                )
                .then((response) => {
                    console.log({ response });
                    addToast("Password successfully updated", {
                        appearance: "success",
                        autoDismiss: true,
                    });
                })
                .catch((error) => {
                    console.log({ error });
                    if (error.response.status === 422) {
                        Object.keys(error.response.data.errors).forEach(
                            (key) => {
                                setErrors({
                                    [key]: error.response.data.errors[key][0],
                                });
                            }
                        );
                    } else {
                        addToast("Unable to process request", {
                            appearance: "error",
                            autoDismiss: true,
                        });
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
    });

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleClickShowNewPassword = () =>
        setShowNewPassword((newPassword) => !newPassword);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleCurrentPasswordChange = (e) => {
        formik.setFieldValue("current", e.target.value, true);
    };

    const handleNewPasswordChange = (e) => {
        formik.setFieldValue("password", e.target.value, true);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <div
                className="payout-pref-container"
                style={{ marginBottom: "15px" }}
            >
                <div className="row">
                    <div
                        className="pass-bx col-lg-6"
                        css={css`
                            margin-bottom: 15px;
                        `}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            disabled={isLoading}
                            name="currentPassword"
                            id="currentPassword"
                            // onBlur={formik.handleBlur}
                            onChange={handleCurrentPasswordChange}
                            value={formik.values.current}
                            error={
                                !!formik.errors.current &&
                                formik.touched.current
                            }
                            helperText={
                                formik.errors.current && formik.touched.current
                                    ? formik.errors.current
                                    : ""
                            }
                            type={showPassword ? "text" : "password"}
                            label="Current Password"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={
                                                handleMouseDownPassword
                                            }
                                            edge="end"
                                        >
                                            {!showPassword ? (
                                                <i className="fa-solid fa-eye-slash"></i>
                                            ) : (
                                                <i className="fa-solid fa-eye"></i>
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                borderRadius: "10px",
                                "& .MuiOutlinedInput-input": {
                                    borderRadius: "10px",
                                },
                                "& .MuiInputBase-root": {
                                    borderRadius: "10px",
                                },
                            }}
                        />
                    </div>
                    <div
                        className="pass-bx col-lg-6"
                        css={css`
                            margin-bottom: 15px;
                        `}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            disabled={isLoading}
                            name="newPassword"
                            id="newPassword"
                            // onBlur={formik.handleBlur}
                            onChange={handleNewPasswordChange}
                            value={formik.values.password}
                            error={
                                !!formik.errors.password &&
                                formik.touched.password
                            }
                            helperText={
                                formik.errors.password &&
                                formik.touched.password
                                    ? formik.errors.password
                                    : ""
                            }
                            type={showNewPassword ? "text" : "password"}
                            label="New Password"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowNewPassword}
                                            onMouseDown={
                                                handleMouseDownPassword
                                            }
                                            edge="end"
                                        >
                                            {!showNewPassword ? (
                                                <i className="fa-solid fa-eye-slash"></i>
                                            ) : (
                                                <i className="fa-solid fa-eye"></i>
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                borderRadius: "10px",
                                "& .MuiOutlinedInput-input": {
                                    borderRadius: "10px",
                                },
                                "& .MuiInputBase-root": {
                                    borderRadius: "10px",
                                },
                            }}
                        />
                    </div>
                </div>
                <div>
                    <button
                        type="submit"
                        className="theme-btn theme-btn-bordered btn btn-primary"
                        disabled={isLoading}
                        style={{
                            borderRadius: "10px",
                        }}
                    >
                        {isLoading ? "Changing Password..." : "Change Password"}
                    </button>
                    <div style={{ marginTop: "10px" }}>
                        Forgot your current password? Logout and reset your
                        password from the login page.
                    </div>
                </div>
            </div>
        </form>
    );
};

export default React.memo(PChangePassword);
